import Label from '@/components/elements/Label/Label';
import { ListItem, ListItemContent } from '@/components/elements/lists';
import DataGrid from '@/components/elements/lists/DataGrid';
import MerchantLogo from '@/components/elements/MerchantLogo/MerchantLogo';
import { CompanyAvatar, PersonAvatar } from '@/components/elements/redesign/Avatar';
import Icon from '@/components/icons/Icon';
import Tooltip from '@/components/modules/Tooltip';
import SelectedPaymentMethod from '@/components/templates/checkout/SelectedPaymentMethod/SelectedPaymentMethod';
import { getSelectedPaymentDetails } from '@/helpers/checkout';
import useCheckoutExperiment from '@/hooks/experiment/useCheckoutExperiment';
import { _s, _t } from '@/locale';
import { SelectedPaymentMethod as PaymentMethod } from '@/types/checkout';
import { createRef, Fragment, useRef, useState } from 'react';

export type CheckoutDetailsGiftcard = {
  code: string;
  amount: string;
  label: string;
  expiryDate?: string;
  onRemove?: (code: string) => void;
};

export type CheckoutDetailsService = {
  name: string;
  price: string;
  duration: string;
  discount?: string;
  amount?: number;
  onRemove?: () => void;
};

export type CheckoutDetailsBundle = {
  name: string;
  price: string;
  quantity: string;
  validity: string;
  discount?: string;
};

export type CheckoutDetailsFee = {
  type: 'onlinePayment';
  amount: string;
};

export type CheckoutDetailsProps = {
  logo: string;
  placeName: string;
  dateTime: string;
  performer: string;
  performerAvatar?: string;
  priceToPay: { label: string; price: string };
  information: string;
  paymentMethod?: PaymentMethod;
  services?: CheckoutDetailsService[];
  bundles?: CheckoutDetailsBundle[];
  giftcards?: CheckoutDetailsGiftcard[];
  fees?: CheckoutDetailsFee[];
};

export const baseTranslationKey = 'components.modules.checkout.CheckoutDetails';

const CheckoutDetails = ({
  logo,
  placeName,
  dateTime,
  performer,
  performerAvatar,
  priceToPay,
  information,
  paymentMethod,
  services,
  giftcards,
  bundles,
  fees,
}: CheckoutDetailsProps) => {
  const [openTooltipIndex, setOpenTooltipIndex] = useState<number | null>(null);
  const toolTipTriggerRefs = useRef<React.RefObject<HTMLButtonElement>[]>([]);
  const toolTipTargetRefs = useRef<React.RefObject<HTMLDivElement>[]>([]);
  const { variant: checkoutVariant } = useCheckoutExperiment();
  const isCheckoutRedesign = checkoutVariant !== 'control';

  const { label } = paymentMethod ? getSelectedPaymentDetails(paymentMethod) : { label: '' };
  const paymentMethodLabel = label;

  if (isCheckoutRedesign) {
    if (priceToPay.label === _s('from')) {
      priceToPay.label = _s('total');
      priceToPay.price = _s('Fr.') + ' ' + priceToPay.price;
    }
    if (priceToPay.price === _s('freePrice')) {
      priceToPay.price = '0 kr';
    }
  }

  (fees ?? []).forEach((_, i) => {
    if (!toolTipTriggerRefs.current[i]) toolTipTriggerRefs.current[i] = createRef<HTMLButtonElement>();
    if (!toolTipTargetRefs.current[i]) toolTipTargetRefs.current[i] = createRef<HTMLDivElement>();
  });

  return (
    <div>
      {isCheckoutRedesign ? (
        <ListItem
          type="div"
          verticalAlign="top"
          rightSlot={<MerchantLogo alt="" size="md" variant="default" imgUrl={logo} />}
          underline>
          <div className="space-y-sm">
            <p className="text-md text-black-900 line-clamp-[2] overflow-hidden text-ellipsis">{placeName}</p>
            <div className="flex items-center justify-between">
              <div className="gap-xs flex items-center">
                <PersonAvatar alt="" size="sm" imgUrl={performerAvatar} />
                <p className="text-m text-fg_secondary line-clamp-[1] overflow-hidden text-ellipsis">{performer}</p>
              </div>
              <p className="text-fg_secondary text-m pr-sm whitespace-nowrap">{dateTime}</p>
            </div>
          </div>
        </ListItem>
      ) : (
        <>
          <ListItem
            type="div"
            verticalAlign="top"
            leftSlot={<CompanyAvatar alt="" size="md" variant="default" imgUrl={logo} />}
            underline>
            <div className="space-y-xs">
              <p className="text-md text-black-900">{placeName}</p>
              <p className="text-black-600 text-m">{dateTime}</p>
              <p className="text-black-900 text-sm">{performer}</p>
            </div>
          </ListItem>
          {information && (
            <ListItem type="div" underline leftSlot={<Icon variant="info-circle" />}>
              <ListItemContent content={information} />
            </ListItem>
          )}
        </>
      )}

      <div className="px-lg">
        <DataGrid ariaLabel={_s(`${baseTranslationKey}.ariaLabel.title`)}>
          {services?.map((service, index) => {
            const isLastService = index === services.length - 1;
            const discount = service.discount;
            const amount = service?.amount ?? 0;
            const translationKey = amount === 1 ? 'usageRemaining' : 'usagesRemaining';
            const servicePrice = isCheckoutRedesign ? service.price.replace(_s('from'), _s('Fr.')) : service.price;
            return isCheckoutRedesign ? (
              <Fragment key={service.name + index}>
                <DataGrid.Row key={index} hasPaddingTop>
                  <DataGrid.Cell
                    title={service.name}
                    type="default"
                    ariaLabel={_s(`${baseTranslationKey}.ariaLabel.service.name`)}
                    truncate
                  />
                  <DataGrid.Cell
                    title={discount ? <span className="text-fg_highlight">{discount}</span> : servicePrice}
                    align="right"
                    ariaLabel={_s(`${baseTranslationKey}.ariaLabel.service.price`)}
                    nowrap
                  />
                </DataGrid.Row>

                <DataGrid.Row undeline>
                  {service.duration && (
                    <DataGrid.Cell
                      title={
                        <span className="gap-sm flex flex-nowrap items-center">
                          <span className="text-nowrap">{service.duration}</span>
                          {service?.onRemove && (
                            <button
                              className="text-fg_negative text-nowrap underline"
                              onClick={() => service.onRemove()}>
                              {_s(`${baseTranslationKey}.removePunchcard`)}
                            </button>
                          )}
                        </span>
                      }
                      type="description"
                      ariaLabel={_s(`${baseTranslationKey}.ariaLabel.service.duration`)}
                    />
                  )}
                  {discount && (
                    <DataGrid.Cell
                      title={<span className="text-fg_secondary line-through">{servicePrice}</span>}
                      type="description"
                      ariaLabel={_s(`${baseTranslationKey}.ariaLabel.service.price`)}
                      align="right"
                    />
                  )}
                  {service?.amount && (
                    <DataGrid.Cell
                      title={
                        <span className="text-nowrap text-fg_secondary">
                          {_s(`bundleCheckout.${translationKey}`, { amount })}
                        </span>
                      }
                      type="description"
                      ariaLabel={_s(`${baseTranslationKey}.ariaLabel.service.duration`)}
                      align="right"
                    />
                  )}
                </DataGrid.Row>
              </Fragment>
            ) : (
              <Fragment key={service.name + index}>
                <DataGrid.Row key={index} hasPaddingTop>
                  <DataGrid.Cell
                    title={service.name}
                    type="muted"
                    ariaLabel={_s(`${baseTranslationKey}.ariaLabel.service.name`)}
                  />
                  <DataGrid.Cell
                    title={
                      discount ? (
                        <span className="text-highlight-700">
                          {discount} <span className="text-black-600 line-through">({service.price})</span>
                        </span>
                      ) : (
                        service.price
                      )
                    }
                    align="right"
                    ariaLabel={_s(`${baseTranslationKey}.ariaLabel.service.price`)}
                  />
                </DataGrid.Row>
                {service.duration && (
                  <DataGrid.Row {...(isLastService && { undeline: true })}>
                    <DataGrid.Cell
                      title={service.duration}
                      type="description"
                      ariaLabel={_s(`${baseTranslationKey}.ariaLabel.service.duration`)}
                    />
                  </DataGrid.Row>
                )}
              </Fragment>
            );
          })}

          {bundles?.map(({ name, price, quantity, validity, discount }, key) => {
            const isLastBundle = key === bundles.length - 1;
            return isCheckoutRedesign ? (
              <Fragment key={name + key}>
                <DataGrid.Row key={key} hasPaddingTop>
                  <DataGrid.Cell
                    title={name}
                    type="default"
                    ariaLabel={_s(`${baseTranslationKey}.ariaLabel.service.name`)}
                    truncate
                  />
                  <DataGrid.Cell
                    title={discount ? <span className="text-fg_highlight">{discount}</span> : price}
                    align="right"
                    ariaLabel={_s(`${baseTranslationKey}.ariaLabel.service.price`)}
                    nowrap
                  />
                </DataGrid.Row>
                <DataGrid.Row>
                  <DataGrid.Cell
                    title={quantity}
                    type="description"
                    ariaLabel={_s(`${baseTranslationKey}.ariaLabel.service.price`)}
                  />
                  {discount && (
                    <DataGrid.Cell
                      title={<span className="text-fg_secondary line-through">{price}</span>}
                      type="description"
                      ariaLabel={_s(`${baseTranslationKey}.ariaLabel.service.price`)}
                      align="right"
                    />
                  )}
                </DataGrid.Row>
                {validity && (
                  <DataGrid.Row undeline>
                    <DataGrid.Cell
                      title={validity}
                      type="description"
                      ariaLabel={_s(`${baseTranslationKey}.ariaLabel.service.duration`)}
                    />
                  </DataGrid.Row>
                )}
              </Fragment>
            ) : (
              <Fragment key={name + key}>
                <DataGrid.Row key={key} hasPaddingTop>
                  <DataGrid.Cell
                    title={name}
                    type="muted"
                    ariaLabel={_s(`${baseTranslationKey}.ariaLabel.service.name`)}
                  />
                  <DataGrid.Cell
                    title={
                      discount ? (
                        <span className="text-highlight-700">
                          {discount} <span className="text-black-600 line-through">({price})</span>
                        </span>
                      ) : (
                        price
                      )
                    }
                    align="right"
                    ariaLabel={_s(`${baseTranslationKey}.ariaLabel.service.price`)}
                  />
                </DataGrid.Row>
                <DataGrid.Row>
                  <DataGrid.Cell
                    title={quantity}
                    type="description"
                    ariaLabel={_s(`${baseTranslationKey}.ariaLabel.service.price`)}
                  />
                </DataGrid.Row>
                {validity && (
                  <DataGrid.Row {...(isLastBundle && { undeline: true })}>
                    <DataGrid.Cell
                      title={validity}
                      type="description"
                      ariaLabel={_s(`${baseTranslationKey}.ariaLabel.service.duration`)}
                    />
                  </DataGrid.Row>
                )}
              </Fragment>
            );
          })}

          {isCheckoutRedesign &&
            (fees ?? []).map((fee, index) => {
              return (
                <div className="relative" ref={toolTipTargetRefs.current[index]} key={index}>
                  <DataGrid.Row undeline hasPaddingTop>
                    <DataGrid.Cell
                      title={
                        <div className="gap-xs flex items-center">
                          <span>{_t(`${baseTranslationKey}.fee.${fee.type}.label`)}</span>
                          <span className="flex">
                            <button
                              className="w-max cursor-pointer outline-none focus:outline-none"
                              ref={toolTipTriggerRefs.current[index]}
                              aria-expanded={openTooltipIndex === index}
                              aria-controls="popover"
                              onClick={(e) => {
                                e.stopPropagation();
                                setOpenTooltipIndex(index);
                              }}>
                              <Icon variant="info-circle" size="sm" color="fg_info" />
                            </button>
                          </span>
                        </div>
                      }
                      type="default"
                    />
                    <DataGrid.Cell
                      title={fee.amount}
                      type="header"
                      align="right"
                      ariaLabel={_s(`${baseTranslationKey}.ariaLabel.fee.label`)}
                    />
                  </DataGrid.Row>
                  {openTooltipIndex === index && (
                    <Tooltip
                      placement="bottom"
                      onClose={() => setOpenTooltipIndex(null)}
                      triggerRef={toolTipTriggerRefs.current[index]}
                      targetRef={toolTipTargetRefs.current[index]}
                      offsetLeft={10}
                      offsetTop={30}
                      onClickOutside={(e) => {
                        if (e) {
                          e.stopPropagation();
                          e.preventDefault();
                        }
                        setOpenTooltipIndex(null);
                      }}>
                      <div className="p-lg space-y-md">
                        <p className="text-s text-fg_primary_inverse">
                          {_t(`${baseTranslationKey}.fee.${fee.type}.info`, { fee: fee.amount })}
                        </p>
                      </div>
                    </Tooltip>
                  )}
                </div>
              );
            })}

          {giftcards?.map(({ code, amount, expiryDate, label, onRemove }) => {
            return isCheckoutRedesign ? (
              <Fragment key={code}>
                <DataGrid.Row hasPaddingTop>
                  <DataGrid.Cell title={label} type="default" />
                  <DataGrid.Cell
                    title={amount}
                    align="right"
                    type="default"
                    ariaLabel={_s(`${baseTranslationKey}.ariaLabel.giftcard.discount`)}
                  />
                </DataGrid.Row>
                <DataGrid.Row undeline>
                  <DataGrid.Cell
                    title={
                      <span className="gap-sm flex flex-nowrap items-center">
                        <span className="text-nowrap">{code}</span>
                        {onRemove && (
                          <button className="text-fg_negative text-nowrap underline" onClick={() => onRemove(code)}>
                            {_s(`${baseTranslationKey}.giftcard.removeGiftcard`)}
                          </button>
                        )}
                      </span>
                    }
                    type="description"
                    ariaLabel={_s(`${baseTranslationKey}.ariaLabel.giftcard.code`)}
                  />
                  <DataGrid.Cell
                    title={
                      <span className="text-fg_secondary text-m">
                        {expiryDate ? ` ${_s('expires')}: ${expiryDate}` : ''}
                      </span>
                    }
                    type="muted"
                    align="right"
                    ariaLabel={_s(`${baseTranslationKey}.ariaLabel.giftcard.code`)}
                  />
                </DataGrid.Row>
              </Fragment>
            ) : (
              <Fragment key={code}>
                <DataGrid.Row hasPaddingTop>
                  <DataGrid.Cell title={label} type="muted" />
                  <DataGrid.Cell
                    title={amount}
                    align="right"
                    type="discount"
                    ariaLabel={_s(`${baseTranslationKey}.ariaLabel.giftcard.discount`)}
                    {...(onRemove && {
                      rightIcon: (
                        <button
                          className="flex items-center justify-center rounded-full duration-75 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#51a49266]"
                          onClick={() => onRemove(code)}>
                          <Icon variant="trash" />
                          <span className="sr-only">{_s(`${baseTranslationKey}.giftcard.removeGiftcard`)}</span>
                        </button>
                      ),
                    })}
                  />
                </DataGrid.Row>
                <DataGrid.Row undeline>
                  <DataGrid.Cell
                    title={`${code}${expiryDate ? ` ${_s('expires')}: ${expiryDate}` : ''}`}
                    type="description"
                    ariaLabel={_s(`${baseTranslationKey}.ariaLabel.giftcard.code`)}
                  />
                </DataGrid.Row>
              </Fragment>
            );
          })}

          <div className="mt-xs"></div>
          <DataGrid.Row hasPaddingTop>
            <DataGrid.Cell title={priceToPay.label} type="header" />
            <DataGrid.Cell
              title={priceToPay.price}
              type="header"
              align="right"
              ariaLabel={_s(`${baseTranslationKey}.ariaLabel.subtotal.amount`)}
            />
          </DataGrid.Row>
          {paymentMethod &&
            (isCheckoutRedesign ? (
              <DataGrid.Row data-cy={`selectedPaymentMethod-${paymentMethod.type}`}>
                <DataGrid.Cell
                  title={_s(`${baseTranslationKey}.ariaLabel.selectedPaymentMethod`)}
                  type="description"
                  ariaLabel={_s(`${baseTranslationKey}.ariaLabel.selectedPaymentMethod`)}
                  truncate
                />
                <DataGrid.Cell
                  title={
                    <Label
                      className="bg-bg_secondary text-fg_secondary whitespace-nowrap"
                      label={paymentMethodLabel}
                      size="lg"
                    />
                  }
                  align="right"
                  type="description"
                  ariaLabel={_s(`${baseTranslationKey}.ariaLabel.selectedPaymentMethod`)}
                />
              </DataGrid.Row>
            ) : (
              <SelectedPaymentMethod method={paymentMethod} />
            ))}
          {information && isCheckoutRedesign && (
            <DataGrid.Row>
              <DataGrid.Cell
                title={_s(`${baseTranslationKey}.ariaLabel.freeCancelBefore`)}
                type="description"
                ariaLabel="can cancel before"
                truncate
              />
              <DataGrid.Cell
                title={
                  <Label
                    className="bg-bg_secondary text-fg_secondary whitespace-nowrap"
                    label={information}
                    size="lg"
                  />
                }
                align="right"
                type="description"
                ariaLabel="information"
              />
            </DataGrid.Row>
          )}
        </DataGrid>
      </div>
    </div>
  );
};

export default CheckoutDetails;
